import { HDate } from '@hebcal/core';
import { MAZALOT, HEBREW_MONTH_NAMES, dayToHebrew, type MazalInfo } from '../data/kabbalisticData';

export interface HebrewNatalData {
  // Gregorian
  gregorianDate: string;
  birthTime: string;
  birthPlace: string;
  // Hebrew date
  hebrewYear: number;
  hebrewMonth: number;
  hebrewDay: number;
  hebrewMonthName: { english: string; spanish: string; hebrew: string };
  hebrewDayHebrew: string;
  hebrewDateFormatted: string;   // "יד סיוון תשפ״ו"
  hebrewDateLatinized: string;   // "14 Siván 5786"
  isLeapYear: boolean;
  // Shabbat / parasha context
  dayOfWeek: string;
  dayOfWeekHebrew: string;
  // Kabbalistic profile
  mazal: MazalInfo;
  // Nightfall note
  afterSunsetNote: boolean;
}

const DAYS_OF_WEEK_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAYS_OF_WEEK_HE = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

export function calculateNatalChart(
  birthDate: string,  // YYYY-MM-DD
  birthTime: string,  // HH:MM
  birthPlace: string
): HebrewNatalData {
  const [yearStr, monthStr, dayStr] = birthDate.split('-');
  const [hourStr] = birthTime.split(':');
  const hour = parseInt(hourStr, 10);

  const jsDate = new Date(
    parseInt(yearStr, 10),
    parseInt(monthStr, 10) - 1,  // JS months are 0-indexed
    parseInt(dayStr, 10)
  );

  // If born after ~7 PM, in Jewish law the new Hebrew day has begun
  // We advance by one day for the Hebrew calendar calculation
  const afterSunset = hour >= 19;
  if (afterSunset) {
    jsDate.setDate(jsDate.getDate() + 1);
  }

  const hd = new HDate(jsDate);
  const hebrewMonth = hd.getMonth();     // 1=Nisan, 7=Tishrei
  const hebrewDay = hd.getDate();
  const hebrewYear = hd.getFullYear();
  const isLeapYear = HDate.isLeapYear(hebrewYear);

  const monthNames = HEBREW_MONTH_NAMES[hebrewMonth] ?? { english: '?', spanish: '?', hebrew: '?' };
  const mazal = MAZALOT[hebrewMonth];

  const dayHebrew = dayToHebrew(hebrewDay);

  // Format the Hebrew year in gematria (e.g. 5786 → תשפ״ו)
  const hebrewYearStr = hebrewYearGematria(hebrewYear);

  const gregJs = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, parseInt(dayStr, 10));
  const dow = gregJs.getDay(); // 0=Sun

  return {
    gregorianDate: birthDate,
    birthTime,
    birthPlace,
    hebrewYear,
    hebrewMonth,
    hebrewDay,
    hebrewMonthName: monthNames,
    hebrewDayHebrew: dayHebrew,
    hebrewDateFormatted: `${dayHebrew} ${monthNames.hebrew} ${hebrewYearStr}`,
    hebrewDateLatinized: `${hebrewDay} ${monthNames.spanish} ${hebrewYear}`,
    isLeapYear,
    dayOfWeek: DAYS_OF_WEEK_ES[dow],
    dayOfWeekHebrew: DAYS_OF_WEEK_HE[dow],
    mazal,
    afterSunsetNote: afterSunset,
  };
}

// Convert Hebrew year number to gematria string (e.g. 5786 → "תשפ״ו")
function hebrewYearGematria(year: number): string {
  // We use the last 3 digits of the millennium (e.g. 5786 → 786)
  const y = year % 1000;
  const hundreds = Math.floor(y / 100);
  const tens = Math.floor((y % 100) / 10);
  const units = y % 10;

  const hundredsLetters = ['', 'ק', 'ר', 'ש', 'ת', 'תק', 'תר', 'תש', 'תת', 'תתק'];
  const tensLetters     = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
  const unitsLetters    = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];

  let str = (hundredsLetters[hundreds] ?? '') + (tensLetters[tens] ?? '') + (unitsLetters[units] ?? '');

  // Insert geresh/gershayim
  if (str.length === 1) {
    str += '׳';
  } else {
    str = str.slice(0, -1) + '״' + str.slice(-1);
  }

  return str;
}
